// src/cockpit4/components/LoginPanel.tsx
// ============================================
// 登录界面 - XEAOS 超级管理员/员工登录
//
// 超级管理员：xiaoshe / 手机号 18670323716
// 密码：存放于 Obsidian Vault XEAOS/Config/AdminConfig.md（白皮书铁律一，禁止硬编码）
// 普通员工：由超级管理员在系统内添加
// ============================================

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Lock, User, Eye, EyeOff, ShieldCheck, AlertCircle, BookOpen } from "lucide-react";
import { useUserStore } from "../../stores/userStore";
import { useCockpit4Store } from "../store";

export default function LoginPanel() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showFirstRunHint, setShowFirstRunHint] = useState(false);

  const { login, loginAsGuest, hydrate, hydrated, currentUser, resetSuperAdminPassword, adminPasswordGenerated } = useUserStore();
  const setWorkspace = useCockpit4Store((s) => s.setWorkspace);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  // 首次运行：若本次会话自动生成了管理员初始密码，展示提示（密码位于 Vault）
  useEffect(() => {
    if (adminPasswordGenerated) {
      setShowFirstRunHint(true);
    }
  }, [adminPasswordGenerated]);

  // 登录处理：必须严格使用输入框的用户名和密码，禁止任何一键登录/绕过机制
  const handleLogin = async () => {
    if (!username.trim() || !password.trim()) {
      setError("请输入用户名和密码");
      return;
    }

    setLoading(true);
    setError("");

    // 模拟短暂加载（避免暴力破解感知）
    await new Promise((r) => setTimeout(r, 300));

    // login 内部会做 normalizeInput（全角转半角、去空格）
    // 严格使用输入框值，禁止绕过输入框
    const result = await login({ username, password });
    if (!result.success) {
      // 登录失败时提示可能的原因（包括浏览器自动填充）
      setError(result.error || "登录失败");
      setLoading(false);
    }
    // 成功时 store 会自动更新 currentUser，App 会重新渲染
  };

  // 重置超级管理员密码（忘记密码时使用，生成新随机密码并写回 Vault）
  const handleResetPassword = async () => {
    if (!confirm("确认重置超级管理员密码为新的随机密码？\n\n新密码将写入 Obsidian Vault：XEAOS/Config/AdminConfig.md\n\n重置后请使用新密码登录（可在 Obsidian 中查看该文件）。")) {
      return;
    }
    const result = await resetSuperAdminPassword();
    if (result.success) {
      setError("");
      setPassword(result.password);
      setUsername("xiaoshe");
      setShowFirstRunHint(true);
      alert(`超级管理员密码已重置。\n\n新密码已写入 Vault：XEAOS/Config/AdminConfig.md\n\n（本提示仅在本次会话显示一次，密码不会写入代码/日志。）`);
    } else {
      alert("重置失败，请确认 Vault 可写后重试。");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleLogin();
  };

  // 已登录或正在恢复会话时不渲染登录页
  if (!hydrated || currentUser) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-[#0B1220] via-[#1A2332] to-[#0B1220]">
      {/* 背景装饰：网格扫描线 */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(59,130,246,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.5) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      {/* 角落光晕 */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative z-10 w-full max-w-md mx-4"
      >
        {/* 头部 Logo */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-blue-500/30 mb-4"
          >
            <ShieldCheck size={32} className="text-blue-400" />
          </motion.div>
          <h1 className="text-2xl font-bold text-white tracking-wide">XEAOS</h1>
          <p className="text-sm text-gray-400 mt-1">企业 AI 操作系统</p>
        </div>

        {/* 登录卡片 */}
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-xl p-8 shadow-2xl">
          {/* 隐藏的假输入框：吸收浏览器自动填充，防止真实密码框被错误填充 */}
          <input type="text" name="fake-username" autoComplete="off" className="hidden" tabIndex={-1} aria-hidden="true" />
          <input type="password" name="fake-password" autoComplete="off" className="hidden" tabIndex={-1} aria-hidden="true" />

          {/* 用户名输入 */}
          <div className="mb-4">
            <label className="block text-xs font-mono text-gray-400 mb-2 uppercase tracking-wider">
              用户名 / 手机号
            </label>
            <div className="relative">
              <User
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
              />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="输入用户名或手机号"
                className="w-full pl-10 pr-4 py-3 rounded-lg bg-black/30 border border-white/[0.08] text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-blue-500/50 focus:bg-black/40 transition-colors"
                autoComplete="off"
                name="xeaos-username-off"
              />
            </div>
          </div>

          {/* 密码输入 */}
          <div className="mb-6">
            <label className="block text-xs font-mono text-gray-400 mb-2 uppercase tracking-wider">
              密码
            </label>
            <div className="relative">
              <Lock
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
              />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="输入密码"
                className="w-full pl-10 pr-10 py-3 rounded-lg bg-black/30 border border-white/[0.08] text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-blue-500/50 focus:bg-black/40 transition-colors"
                autoComplete="new-password"
                name="xeaos-password-off"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* 首次运行提示：管理员密码已生成并写入 Vault */}
          {showFirstRunHint && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="mb-4 flex items-start gap-2 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20"
            >
              <BookOpen size={14} className="text-blue-400 flex-shrink-0 mt-0.5" />
              <span className="text-xs text-blue-300">
                首次运行：管理员密码已自动生成，请到 Obsidian Vault 查看
                <code className="mx-1 px-1 py-0.5 rounded bg-blue-500/20 text-[10px]">XEAOS/Config/AdminConfig.md</code>
                （凭据存放于知识库，不写入代码）
              </span>
            </motion.div>
          )}

          {/* 错误提示 */}
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="mb-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20"
            >
              <AlertCircle size={14} className="text-red-400 flex-shrink-0" />
              <span className="text-xs text-red-300">{error}</span>
            </motion.div>
          )}

          {/* 登录按钮 */}
          <button
            onClick={() => handleLogin()}
            disabled={loading}
            className="w-full py-3 rounded-lg bg-gradient-to-r from-blue-500 to-emerald-500 text-white text-sm font-medium hover:from-blue-400 hover:to-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/20"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                />
                登录中...
              </span>
            ) : (
              "登 录"
            )}
          </button>

          {/* v6.3.0 游客体验入口：无需密码进入，仅体验 AI 超市等只读/生成功能 */}
          <button
            onClick={() => { loginAsGuest(); setWorkspace("projects"); }}
            className="w-full mt-3 py-3 rounded-lg border border-white/[0.12] bg-white/[0.03] text-gray-300 text-sm font-medium hover:bg-white/[0.06] hover:text-white transition-all"
          >
            🚀 游客体验（免密进入 AI 超市）
          </button>

          {/* 安全提示：禁止一键登录，必须输入真实凭据 */}
          <div className="mt-4 px-3 py-2 rounded-lg bg-amber-500/[0.04] border border-amber-500/15 text-[10px] text-amber-300/70 font-mono text-center">
            <ShieldCheck size={10} className="inline mr-1 -mt-0.5" />
            安全登录：仅授权用户可访问，凭据错误将被拒绝
          </div>
        </div>

        {/* 底部提示 */}
        <div className="text-center mt-6 space-y-2">
          <p className="text-xs text-gray-600">
            小舍科技 · XEAOS 企业 AI 操作系统 · v4.0
          </p>
          <button
            onClick={handleResetPassword}
            className="text-[10px] text-gray-600 hover:text-blue-400 transition-colors underline-offset-2 hover:underline"
          >
            忘记超级管理员密码？点击重置（新密码写入 Obsidian Vault）
          </button>
        </div>
      </motion.div>
    </div>
  );
}
